/**
 * Guía del Tutor IA (M4, AUTONOMY · Layer 3).
 */
import { msg } from "@lingui/core/macro";
import { Sparkles } from "lucide-solid";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, Steps } from "../components/help-prose";

export const aiTutorGuide: HelpGuide = {
  key: "help.ai_tutor",
  slug: "tutor-ia",
  title: msg`AI Tutor guide`,
  description: msg`An in-app tutor that answers your questions using the course material and gives a preliminary AI draft on your submissions before human grading.`,
  audience: msg`Students, teachers and administrators`,
  icon: Sparkles,
  sections: [
    {
      id: "chat",
      title: msg`Ask the tutor`,
      summary: msg`Ask questions about your course and get answers grounded in the material, any time.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Open the AI Tutor from the menu, or use "Ask the tutor" from a course or lesson.`,
              msg`The tutor answers based on the course material; if the answer is not there, it says so.`,
              msg`It is a study aid — it never invents your grade and does not take actions on your account.`,
            ]}
          />
          <GoTo to="/ai-tutor" label={msg`Open the AI Tutor`} />
        </>
      ),
    },
    {
      id: "feedback",
      title: msg`Preliminary feedback on submissions`,
      summary: msg`Ask the AI for a draft on your submission before your teacher grades it — clearly labeled as a draft, not your grade.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open your submission and choose "Ask for AI feedback".`,
              msg`Read the draft: what is good, what to improve, and suggestions grounded in the assignment and rubric.`,
              msg`Use it to improve before submitting — your real grade is always set by your teacher.`,
            ]}
          />
          <Note msg={msg`The AI feedback is a draft and never becomes an official grade.`} />
        </>
      ),
    },
    {
      id: "disponibilidad",
      title: msg`Availability and quota`,
      summary: msg`Your academy configures the AI provider and a monthly token quota. If it is off, unconfigured or over quota, the tutor shows a friendly notice.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Administrators enable the AI integration and set the model and monthly token cap per organization.`,
              msg`When the quota runs out for the month, the tutor pauses gracefully until the next period.`,
            ]}
          />
          <Note
            msg={msg`Enabling the real AI requires the AI integration configured with an API key in Admin → Integrations.`}
          />
        </>
      ),
    },
  ],
};
